import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteProps, Box, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { UserService } from '../../services/user.service';
import { displayUsername } from '../../utils/userUtils';
import SearchableSelect from './SearchableSelect';

/** Minimal shape every employee picker needs; callers may pass richer objects. */
export interface EmployeeOption {
    id: string;
    username: string;
    fullName?: string;
}

/** How many employees to preload when the box is opened with an empty search. */
export const EMPLOYEE_DEFAULT_PAGE_SIZE = 10;
/** How many matches to fetch once the user has typed something. */
export const EMPLOYEE_SEARCH_PAGE_SIZE = 20;

const SEARCH_DEBOUNCE_MS = 300;
/** Page fetched when results are narrowed client-side by `allowedIds` (the server PageGuard max). */
const ALLOWED_IDS_PAGE_SIZE = 200;

export const employeeLabel = (option: EmployeeOption): string =>
    displayUsername(option.username);

export const employeeLabelWithFullName = (option: EmployeeOption): string =>
    option.fullName?.trim() || displayUsername(option.username);

interface UseEmployeeSearchOptions {
    /** Set false to stop fetching (e.g. the picker is disabled). Defaults to true. */
    enabled?: boolean;
    /** Rows fetched when the search box is empty. Defaults to 10. */
    defaultPageSize?: number;
    /** Rows fetched once the user types. Defaults to 20. */
    searchPageSize?: number;
    /** Hide deactivated accounts (enrolment-style pickers). Defaults to false. */
    activeOnly?: boolean;
    /**
     * When set, only these user ids are offered (e.g. a department head's team). The list
     * endpoint is institution-wide, so a wider page is fetched and narrowed here. Pass a
     * memoised Set: a new instance on every render re-triggers the fetch.
     */
    allowedIds?: ReadonlySet<string> | null;
    onError?: (error: unknown) => void;
}

/**
 * Backs an employee picker: preloads the first page of employees so an empty,
 * freshly-focused box already lists people, then debounce-searches the server as
 * the user types. Use this directly only when you need a non-standard
 * `<Autocomplete>` (multi-select, custom rendering); otherwise use
 * `<EmployeeAutocomplete>` below.
 */
export const useEmployeeSearch = <T extends EmployeeOption = EmployeeOption>({
    enabled = true,
    defaultPageSize = EMPLOYEE_DEFAULT_PAGE_SIZE,
    searchPageSize = EMPLOYEE_SEARCH_PAGE_SIZE,
    activeOnly = false,
    allowedIds = null,
    onError,
}: UseEmployeeSearchOptions = {}) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);

    // Kept in a ref so a caller's inline arrow function does not re-trigger the fetch.
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        if (!enabled) {
            setOptions([]);
            return;
        }
        const search = inputValue.trim();
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const size = allowedIds ? ALLOWED_IDS_PAGE_SIZE : search ? searchPageSize : defaultPageSize;
                // Only pass the flag when set, so default callers keep the original 4-arg call.
                const res = activeOnly
                    ? await UserService.getInstitutionUsers(0, size, undefined, search || undefined, true)
                    : await UserService.getInstitutionUsers(0, size, undefined, search || undefined);
                const rows: T[] = res.content || [];
                if (!cancelled) setOptions(allowedIds ? rows.filter(r => allowedIds.has(r.id)) : rows);
            } catch (err) {
                if (!cancelled) {
                    setOptions([]);
                    onErrorRef.current?.(err);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
            // The initial (empty) load should not wait out the typing debounce.
        }, search ? SEARCH_DEBOUNCE_MS : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [inputValue, enabled, defaultPageSize, searchPageSize, activeOnly, allowedIds]);

    /** Wire straight into `<Autocomplete onInputChange={...} />`. */
    const handleInputChange = useCallback(
        (_event: React.SyntheticEvent, value: string, reason: string) => {
            if (reason === 'input') setInputValue(value);
            else if (reason === 'clear') setInputValue('');
        },
        []
    );

    return { options, loading, inputValue, setInputValue, handleInputChange };
};

/** Keeps the selected value present in the option list so MUI can match it. */
export const withSelected = <T extends EmployeeOption>(
    options: T[],
    selected: T | T[] | null
): T[] => {
    const selection = selected == null ? [] : Array.isArray(selected) ? selected : [selected];
    const missing = selection.filter(s => !options.some(o => o.id === s.id));
    return missing.length ? [...missing, ...options] : options;
};

type PassthroughProps<T extends EmployeeOption> = Partial<
    Pick<AutocompleteProps<T, false, false, false>, 'renderOption' | 'openOnFocus' | 'autoHighlight'>
>;

interface EmployeeAutocompleteProps<T extends EmployeeOption> extends PassthroughProps<T> {
    value: T | null;
    onChange: (value: T | null) => void;
    label?: string;
    placeholder?: string;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    /** Override the option label (e.g. to include a full name). */
    getOptionLabel?: (option: T) => string;
    /** Extra options to keep selectable regardless of the search result. */
    extraOptions?: T[];
    /** Hide deactivated accounts from the options. */
    activeOnly?: boolean;
    /** Offer only these user ids (memoised Set); see `useEmployeeSearch`. */
    allowedIds?: ReadonlySet<string> | null;
    onError?: (error: unknown) => void;
    'data-testid'?: string;
    /** Help-mode key; reaches the DOM through the pass-through props. */
    'data-help'?: string;
}

/**
 * Employee picker used across HR, payroll, attendance and reporting screens.
 *
 * Unlike the old per-screen copies, this shows the first
 * `EMPLOYEE_DEFAULT_PAGE_SIZE` employees as soon as it is focused with an empty
 * box, and narrows to server-side matches once the user types.
 *
 * Frontend UI convention: filter/toolbar controls must never occupy a whole
 * row on their own — lay filter rows out as `display:'flex', flexWrap:'wrap',
 * gap` and give each control a flex cell (`flex: '1 1 <basis>px'` + a
 * `maxWidth`), never a fixed pixel width (kills responsiveness) and never an
 * unconstrained `fullWidth` in a row. `fullWidth` alone is only for stacked
 * dialog/form columns — this component defaults to `fullWidth={true}` (it
 * simply fills its own flex cell); callers in a filter row must constrain
 * that cell via `sx` on their end, not by turning `fullWidth` off here.
 */
export function EmployeeAutocomplete<T extends EmployeeOption = EmployeeOption>({
    value,
    onChange,
    label = 'Employee',
    placeholder = 'Search employees...',
    size = 'small',
    fullWidth = true,
    disabled = false,
    required,
    error,
    helperText,
    sx,
    getOptionLabel = employeeLabelWithFullName as (option: T) => string,
    extraOptions,
    activeOnly,
    allowedIds,
    onError,
    renderOption,
    ...rest
}: EmployeeAutocompleteProps<T>) {
    const { options, loading, inputValue, setInputValue } = useEmployeeSearch<T>({
        enabled: !disabled,
        activeOnly,
        allowedIds,
        onError,
    });

    const mergedOptions = useMemo(() => {
        const base = extraOptions?.length ? withSelected(options, extraOptions) : options;
        return withSelected(base, value);
    }, [options, extraOptions, value]);

    // The input text stays name-only; the dropdown row adds the username so two employees
    // sharing a full name can still be told apart.
    const defaultRenderOption: NonNullable<PassthroughProps<T>['renderOption']> = (props, option) => {
        const { key: _key, ...liProps } = props as typeof props & { key?: React.Key };
        const main = getOptionLabel(option);
        const username = displayUsername(option.username);
        return (
            <li {...liProps} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span>{main}</span>
                    {username && username !== main && (
                        <Typography variant="caption" color="text.secondary">{username}</Typography>
                    )}
                </Box>
            </li>
        );
    };

    return (
        <SearchableSelect<T>
            options={mergedOptions}
            renderOption={renderOption ?? defaultRenderOption}
            value={value ? value.id : ''}
            selectedOption={value}
            onChange={(_id, option) => onChange(option)}
            getOptionLabel={getOptionLabel}
            // The server has already filtered; re-filtering here would hide matches
            // whose label differs from the field the backend searched.
            disableClientFilter
            onInputChange={setInputValue}
            loading={loading}
            label={label}
            placeholder={placeholder}
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            required={required}
            error={error}
            helperText={helperText}
            sx={sx}
            noOptionsText={
                loading ? 'Loading\u2026' : inputValue ? 'No employees found' : 'No employees'
            }
            {...rest}
        />
    );
}

export default EmployeeAutocomplete;
